import { isAnyOf } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { map, isEmpty } from 'lodash';
import { checkVanityUrlAvailability } from '@client/listeners/utils/router';
import { showError } from '@client/listeners/lib/alerts';
import { getSessionUser } from '@client/selectors/helpers/session';
import {
  startPatchVenue,
  startCreateVenue, updateVenue, upsertVenue, startCreatePartialVenue,
} from '@client/slices/entities/venues.reducer';
import { NotificationTypes, ProfileTypes } from '@sda/enums';
import { setLoaderIsLoading, setLoaderIsFinishedSuccess, setLoaderIsFinishedError } from '@client/slices/helpers/loaders.reducer';
import { trackAnalytics } from '@client/intercom';
import { updateCurrentSessionUser } from '@client/slices/helpers/session.reducer';
import { getSanitizedVanityUrl, addToVanityUrlCacheArray, apiUrl } from '@sda/utils/url';
import { getMusicTypes, getVenueTypeLabel } from '@client/selectors/entities/categories';
import { enrichUser, getVanityUrl } from '@client/listeners/utils/session';
import { firebaseRequest, getLocationData, handleRequest } from '@client/listeners/lib/requests';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { AnalyticsEvents } from '@sda/interfaces/queue/analytics';
import type { VetVenueDto } from 'functions/src/modules/user/dto/vet-venue.dto';
import type { UpdateProfileDto } from '@sda/functions/show/put-update-profile.endpoint';
import type { AddListener } from '@client/listeners/lib';
import type { Writable } from 'type-fest';
import type { Venue } from '@sda/interfaces/venue';

export const hostFormListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startPatchVenue,
    effect: async (action, listenerApi) => {
      const profile = action.payload;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;
      const firebase = window.Firebase;

      const scope = 'signup-venue-create-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        const {
          id, description, profileType, publicImageUrl,
          hasLocationOverride, googlePlaceId,
        } = getSelectedProfile(state);

        const venueData = {
          ...profile,
          ...(hasLocationOverride || googlePlaceId === profile.googlePlaceId) ? {} : await getLocationData(profile.googlePlaceId),
          ...await getVanityUrl(listenerApi, profile.name),
        };

        const publicVenue = firebase.SDK.Venues.filter(venueData, firebase.SDK.Venues.PublicMap, true);
        const privateVenue = firebase.SDK.Venues.Private().filter(venueData, firebase.SDK.Venues.PrivateMap, true);

        await firebase.helpers.runTransaction(async () => {
          await firebase.SDK.Venues.propagate(id, publicVenue);
          await firebase.SDK.Venues.Private().propagate(id, privateVenue);
        });

        batch(() => {
          dispatch(setLoaderIsFinishedSuccess(scope));
          dispatch(updateVenue({ id, changes: { ...publicVenue, ...privateVenue } }));
        });

        trackAnalytics(startPatchVenue, { ...venueData });

        // Mark this profile as completed if either of these fields are empty in the previous form state
        if (isEmpty(description) || isEmpty(publicImageUrl)) {
          trackAnalytics(AnalyticsEvents.COMPLETED_PROFILE, {
            profileType,
          });
        }
      } catch (e) {
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),

  addListener({
    matcher: isAnyOf(startCreateVenue, startCreatePartialVenue),
    effect: async (action, listenerApi) => {
      const profile = action.payload as Partial<Venue>;
      const firebase = window.Firebase;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const user = getSessionUser(state);
      const musicTypes = getMusicTypes(state);

      const scope = 'signup-venue-create-form';
      dispatch(setLoaderIsLoading(scope));

      try {
        let venueData: Writable<Partial<Venue>> = {
          ...profile,
        };

        const venueId = firebase.SDK.Venues.getNextId();
        const currentSelectedProfile = getSelectedProfile(state);

        // get available vanityUrl
        let vanityUrl = getSanitizedVanityUrl(profile.name);
        vanityUrl = await checkVanityUrlAvailability({ objectType: ProfileTypes.VENUE, vanityUrl });

        const vanityUrlLowercase = vanityUrl.toLowerCase();

        venueData = {
          ...venueData,
          vanityUrl,
          vanityUrlLowercase,
          vanityUrlCache: addToVanityUrlCacheArray([], vanityUrlLowercase),
          preferredMusicTypes: map(musicTypes, (type) => type.id),
          fullName: `${user.firstName} ${user.lastName}`,
          hostEmail: user.email,
          owner: user.id,
          isVetted: false,
          collaborators: [
            user.id,
          ],
        };

        const location = await getLocationData(venueData.googlePlaceId);

        venueData = {
          ...venueData,
          ...location || {},
          timezone: location?.timezoneOverride || null,
        };

        const userData = {
          selectedProfileId: venueId,
          selectedProfileType: ProfileTypes.VENUE,
        };

        const publicVenue = firebase.SDK.Venues.filter(venueData, firebase.SDK.Venues.PublicMap);
        const privateVenue = firebase.SDK.Venues.Private().filter(venueData, firebase.SDK.Venues.PrivateMap);

        await firebase.helpers.runTransaction(async () => {
          await firebase.SDK.Venues.set(venueId, publicVenue);
          await firebase.SDK.Venues.Private().set(venueId, privateVenue);
          await firebase.SDK.Users.Private().update(venueData.owner, userData);

          await firebase.SDK.Notifications.addNotification(
            user.id,
            NotificationTypes.NOTIFICATION_TYPE_ACCOUNT_UPDATE,
            {
              profileType: 'Host',
              profileName: venueData.name,
              template: 'ACCOUNT_CREATE_PROFILE',
            },
          );
        });

        if (currentSelectedProfile?.profileType === ProfileTypes.PENDING) {
          const request = await firebaseRequest<UpdateProfileDto>({
            url: apiUrl('user/replaceProfile'),
            method: 'PUT',
            body: {
              pendingProfileId: currentSelectedProfile.id,
              targetProfileId: venueId,
              targetProfileType: ProfileTypes.VENUE,
            },
          });

          listenerApi.fork(async () => {
            await handleRequest(request);
          });
        }

        listenerApi.fork(async () => {
          const request = await firebaseRequest<VetVenueDto>({
            url: apiUrl('user/vetVenue/'),
            method: 'PUT',
            body: {
              venueId,
            },
          });

          await handleRequest(request);
        });

        batch(() => {
          dispatch(setLoaderIsFinishedSuccess(scope));
          dispatch(upsertVenue({ id: venueId, ...publicVenue, ...privateVenue }));
          dispatch(updateCurrentSessionUser(userData));
        });

        listenerApi.fork(async () => {
          await enrichUser(listenerApi);
        });

        trackAnalytics(startCreateVenue, {
          isPartialProfile: startCreatePartialVenue.match(action),
          profileType: 'Venue',
          ...venueData,
        });

        let venueType = getVenueTypeLabel(state, venueData.venueType);

        if (venueType) {
          venueType = venueType.toLowerCase();
        }

        // This is a little hacky but we need to send specific events to Intercom rather than relying on properties
        if (venueType === 'personal residence') {
          trackAnalytics(AnalyticsEvents.CREATED_PERSONAL_RESIDENCE);
        } else if (venueType === 'commercial space') {
          trackAnalytics(AnalyticsEvents.CREATED_COMMERCIAL_SPACE);
        } else if (venueType === 'community space') {
          trackAnalytics(AnalyticsEvents.CREATED_COMMUNITY_SPACE);
        }
      } catch (e) {
        console.log(e);
        showError(e);
        dispatch(setLoaderIsFinishedError(scope, e));
      }
    },
  }),
];
